// src/pages/BillingSettings.js

import React, { useEffect, useState } from "react";
import axios from "axios";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const BillingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("");
  const [cycleMonths, setCycleMonths] = useState(3);
  const [graceDays, setGraceDays] = useState(0);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Base API URL (same style as AdminDashboard)
  const API_BASE = process.env.REACT_APP_API_URL; // e.g. "http://localhost:8000/api/"
  const token = localStorage.getItem("token");

  // Common config WITH auth header
  const authConfig = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : {};

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(
        `${API_BASE}library/billing-settings/`,
        authConfig
      );

      const apiMode = res.data.mode;
      const apiCycle = res.data.default_cycle_months;

      setMode(apiMode || "");

      // Agar backend se 0/undefined aaye to thoda sane default:
      if (apiMode === "rolling_monthly" || apiMode === "calendar_monthly") {
        // Rolling / Calendar => 1 month cycle
        setCycleMonths(apiCycle || 1);
      } else if (apiMode === "fixed_tenure") {
        // Fixed tenure => 3/6/9/12 me se koi
        if ([1, 2, 3, 6, 9, 12].includes(apiCycle)) {
          setCycleMonths(apiCycle);
        } else {
          setCycleMonths(3);
        }
      } else {
        setCycleMonths(apiCycle || 1);
      }

      setGraceDays(res.data.invoice_grace_days);
    } catch (err) {
      console.error(err);
      setError("Failed to load billing settings.");
    } finally {
      setLoading(false);
    }
  };

  // 🔁 Mode change handler:
  // - Rolling / Calendar => cycleMonths = 1
  // - Fixed tenure => 3/6/9/12, agar current invalid hai to 3
  const handleModeChange = (e) => {
    const value = e.target.value;
    setMode(value);

    if (value === "rolling_monthly" || value === "calendar_monthly") {
      setCycleMonths(1);
    }

    if (value === "fixed_tenure" && ![1, 2, 3, 6, 9, 12].includes(cycleMonths)) {
      setCycleMonths(3);
    }
  };

  const saveSettings = async () => {
    try {
      setError("");
      setSuccess("");
      setSaving(true);

      // 1️⃣ Check if any approved (active) library bookings exist
      const bookingRes = await axios.get(
        `${API_BASE}library/bookings/?status=approved`,
        authConfig
      );

      if (Array.isArray(bookingRes.data) && bookingRes.data.length > 0) {
        const msg =
          "You cannot change the billing settings while active library bookings exist. Please clear all active bookings first.";
        setError(msg);
        alert(msg);
        return; // stop here
      }

      // 2️⃣ No active bookings → allow updating billing settings
      const body = {
        mode: mode,
        default_cycle_months: cycleMonths,
        invoice_grace_days: graceDays,
      };

      await axios.put(
        `${API_BASE}library/billing-settings/`,
        body,
        authConfig
      );

      setSuccess("Billing settings saved successfully.");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        setError("Session expired or unauthorized. Please login again.");
      } else if (err.response?.data?.detail) {
        // backend ka guard message show karo (booking > 0 case etc.)
        setError(err.response.data.detail);
      } else {
        setError("Failed to save billing settings.");
      }
    } finally {
      setSaving(false);
    }
  };

  const getModeLabel = () => {
    if (mode === "rolling_monthly") return "Rolling Monthly";
    if (mode === "calendar_monthly") return "Calendar Monthly";
    if (mode === "fixed_tenure") return "Fixed Tenure";
    return "Not configured";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <p className="text-gray-600 text-sm font-medium">
            Loading billing settings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6">
      {/* Card wrapper */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        {/* Card header bar */}
        <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md">
              <SettingsIcon className="text-white" />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-gray-800">
                Library Billing Settings
              </h3>
              <p className="text-xs md:text-sm text-gray-600">
                Configure global billing behaviour and invoice cycle for all
                library bookings.
              </p>
            </div>
          </div>

          {/* Active mode pill */}
          <div className="hidden sm:flex items-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-blue-100 px-3 py-1 text-xs font-medium text-blue-700 shadow-sm">
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
              <span>Active Mode: {getModeLabel()}</span>
            </span>
          </div>
        </div>

        {/* Card body */}
        <div className="px-4 md:px-6 py-4 md:py-5 space-y-4">
          {/* Alerts */}
          {success && (
            <div className="flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">
              <CheckCircleOutlineIcon className="mt-0.5 text-green-500" />
              <span>{success}</span>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              <ErrorOutlineIcon className="mt-0.5 text-red-500" />
              <span>{error}</span>
            </div>
          )}

          {/* Info strip */}
          <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs md:text-sm text-blue-800">
            <InfoOutlinedIcon className="mt-0.5 text-blue-500" />
            <p>
              Billing settings affect how invoices are generated, how expiry is
              calculated and how renewal works across the entire Study Center.
              Change these carefully and only when there are no active
              bookings.
            </p>
          </div>

          {/* Form grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            {/* Billing mode */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">
                Billing Mode
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                value={mode}
                onChange={handleModeChange}
              >
                <option value="rolling_monthly">Rolling Monthly</option>
                <option value="calendar_monthly">Calendar Monthly</option>
                <option value="fixed_tenure">
                  Fixed Tenure (1 / 2 / 3 / 6 / 9 / 12 Months)
                </option>
              </select>
              <p className="text-xs text-gray-500 flex items-start gap-1">
                <span className="mt-[2px]">•</span>
                <span>
                  Controls whether invoices follow calendar month, booking start
                  date, or a fixed multi-month tenure.
                </span>
              </p>
            </div>

            {/* Tenure duration (only for fixed tenure) */}
            {mode === "fixed_tenure" && (
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Tenure Duration
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  value={cycleMonths}
                  onChange={(e) => setCycleMonths(Number(e.target.value))}
                >
                  <option value={1}>1 Month(Only for 11th and 12th Students)</option>
                  <option value={2}>2 Months(Only for 11th and 12th Students)</option>
                  <option value={3}>3 Months</option>
                  <option value={6}>6 Months</option>
                  <option value={9}>9 Months</option>
                  <option value={12}>12 Months</option>
                </select>
                <p className="text-xs text-gray-500 flex items-start gap-1">
                  <span className="mt-[2px]">•</span>
                  <span>
                    For fixed tenure mode, every billing cycle will cover this
                    many months of access.
                  </span>
                </p>
              </div>
            )}

            {/* Grace days */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700">
                Invoice Grace Days
              </label>
              <div className="flex flex-wrap items-center gap-2 max-w-md">
                <input
                  type="number"
                  min={0}
                  className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  value={graceDays}
                  onChange={(e) => setGraceDays(Number(e.target.value))}
                />
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  days
                </span>
                <span className="hidden sm:inline text-xs text-gray-400">
                  (0 = no extra grace)
                </span>
              </div>
              <p className="text-xs text-gray-500 flex items-start gap-1">
                <span className="mt-[2px]">•</span>
                <span>
                  Extra days after the end of the billing period before an
                  invoice is treated as expired and the seat becomes unpaid.
                </span>
              </p>
            </div>
          </div>

          {/* Save button */}
          <div className="pt-2 flex justify-end">
            <button
              onClick={saveSettings}
              disabled={saving}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md hover:shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-150 ${
                saving ? "opacity-75 cursor-not-allowed" : ""
              }`}
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <SettingsIcon className="text-sm" />
                  <span>Save Settings</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingSettings;
